import { RTU } from '../../../types/RTU';
import { RTUNode, TreeNode } from '../../../types/TreeTypes';
import { isMissing } from '../../../utils/rtuDefaults';

/** Display-only group heading; never written onto RTU leaf objects. */
export const UNASSIGNED_GROUP_LABEL = 'Unassigned';

const toGroupDisplayLabel = (value: string | null | undefined): string =>
  isMissing(value) ? UNASSIGNED_GROUP_LABEL : String(value);

/** JSON-encoded tuple so labels with colons or other separators cannot collide. */
const encodeGroupKey = (kind: 'provider' | 'field', ...parts: string[]): string =>
  JSON.stringify([kind, ...parts]);

const createTreeData = (rtus: RTU[]): TreeNode[] => {
  const groupByProvider = rtus.reduce((acc: Record<string, Record<string, RTU[]>>, rtu: RTU) => {
    const providerLabel = toGroupDisplayLabel(rtu.provider);
    const fieldLabel = toGroupDisplayLabel(rtu.field);
    if (!acc[providerLabel]) {
      acc[providerLabel] = {};
    }
    if (!acc[providerLabel][fieldLabel]) {
      acc[providerLabel][fieldLabel] = [];
    }
    acc[providerLabel][fieldLabel].push(rtu);
    return acc;
  }, {});


  const treeData: TreeNode[] = Object.entries(groupByProvider).map(([provider, fields]) => {
    const providerNode: TreeNode = {
      key: encodeGroupKey('provider', provider),
      data: {
        id: '',
        name: '',
        client: '',
        location: '',
        latitude: '',
        longitude: '',
        status: '',
        configVersion: '',
        deploymentVersion: '',
        diskUsage: '',
        cpuUsage: '',
        lastData: '',
        installDate: '',
        publicIPAddress: '',
        localIPAddress: '',
        reportingToServer: null,
        lastConfigUpdate: null,
        groupId: '',
        systemRestart: false,
        provider: provider,
        canonicalStatus: null,
        statusObservedAtUtc: null,
        lastProbeAtUtc: null,
        lastFileReceivedAtUtc: null,
        statusSource: null,
        statusReason: null,
        onlineStatus: null,
      },
      children: Object.entries(fields).map(([field, groupedRtus]): TreeNode => {
        const fieldNode: TreeNode = {
          key: encodeGroupKey('field', provider, field),
          data:  {
            id: '',
            name: '',
            client: '',
            location: '',
            latitude: '',
            longitude: '',
            status: '',
            configVersion: '',
            deploymentVersion: '',
            diskUsage: '',
            cpuUsage: '',
            lastData: '',
            installDate: '',
            publicIPAddress: '',
            localIPAddress: '',
            reportingToServer: null,
            lastConfigUpdate: null,
            groupId: '',
            systemRestart: false,
            provider: field,
            canonicalStatus: null,
            statusObservedAtUtc: null,
            lastProbeAtUtc: null,
            lastFileReceivedAtUtc: null,
            statusSource: null,
            statusReason: null,
            onlineStatus: null,
          },
          children: groupedRtus.map((rtu): RTUNode => ({
            key: rtu.id.toString(),
            data: rtu
          }))
        };
        return fieldNode;
      })
    };
    return providerNode;
  });

  return treeData;
};

export default createTreeData;
